import { useState } from 'react';
import { Separator } from '@/components/ui/separator';
import type {
  BrandFacet,
  ItemsSearchParams,
  PriceBucket,
  PriceStats,
  TagFacet,
} from '../../-types';
import { groupBy } from '../../-utils';
import { ExpandableFilterList } from './expandable-filter-list';
import { FilterSection } from './filter-section';
import { GlobalFilters } from './global-filters';
import { PriceRangeFilter } from './price-range-filter';

const LIMIT = 15;

interface SideFiltersProps {
  brands: BrandFacet[];
  tags: TagFacet[];
  priceStats: PriceStats;
  priceHistogram: PriceBucket[];
  currentParams: ItemsSearchParams;
  onParamsChange: (updates: Partial<ItemsSearchParams>) => void;
}

export function SideFilters({
  brands,
  tags,
  priceStats,
  priceHistogram,
  currentParams,
  onParamsChange,
}: SideFiltersProps) {
  // Track which sections are open
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([-2, -1, 0, 1, 2, 3, 4]));

  const toggleSection = (id: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Order brands: selected first, then by count
  const orderedBrands = [
    ...brands.filter((b) => currentParams.brands?.includes(b.id)),
    ...brands
      .filter((b) => !currentParams.brands?.includes(b.id))
      .sort((a, b) => b.count - a.count),
  ].slice(0, LIMIT);

  // Group tags by key
  const groupedTags = groupBy(tags, (t) => t.keyId);

  // Priority tag ordering
  const firstTags = [215, 58245, 1, 22, 7903];
  const orderedTagGroups = [
    ...firstTags.map((id) => groupedTags[id]).filter((g): g is TagFacet[] => Boolean(g)),
    ...Object.values(groupedTags).filter((group) => !firstTags.includes(group[0]?.keyId)),
  ];

  // Transform brands to filter options
  const brandFilterOptions = orderedBrands.map((brand) => ({
    id: brand.id,
    name: brand.name || 'Unknown Brand',
    count: brand.count,
    checked: currentParams.brands?.includes(brand.id) ?? false,
    onCheckedChange: (checked: boolean) => {
      onParamsChange({
        brands: checked
          ? [...(currentParams.brands || []), brand.id]
          : currentParams.brands?.filter((id) => id !== brand.id),
        brandName: null,
        page: 1,
      });
    },
  }));

  // Transform tag values to filter options
  const createTagFilterOptions = (tagValues: TagFacet[]) =>
    tagValues.map((tag) => ({
      id: tag.valueId,
      name: tag.valueName,
      count: tag.count,
      checked: currentParams.filters?.includes(tag.valueId) ?? false,
      onCheckedChange: (checked: boolean) => {
        onParamsChange({
          filters: checked
            ? [...(currentParams.filters || []), tag.valueId]
            : currentParams.filters?.filter((id) => id !== tag.valueId),
          page: 1,
        });
      },
    }));

  return (
    <div className="sidebar flex h-full flex-col pt-0">
      <div className="mt-5 flex-1 pl-4 overflow-y-auto xl:mt-0">
        {/* Global Filters */}
        <GlobalFilters
          lowestPricesOnly={currentParams.lowestPricesOnly}
          includeOutOfStock={currentParams.includeOutOfStock}
          onLowestPricesChange={(value) => onParamsChange({ lowestPricesOnly: value, page: 1 })}
          onIncludeOutOfStockChange={(value) =>
            onParamsChange({ includeOutOfStock: value, page: 1 })
          }
        />

        <Separator />

        {/* Price Range */}
        {priceStats.min !== undefined &&
          priceStats.max !== undefined &&
          priceStats.min !== priceStats.max && (
            <>
              <FilterSection
                title="טווח מחירים"
                isOpen={openSections.has(-2)}
                onToggle={() => toggleSection(-2)}
              >
                <PriceRangeFilter
                  priceStats={priceStats}
                  priceHistogram={priceHistogram}
                  currentMin={currentParams.minPrice ?? undefined}
                  currentMax={currentParams.maxPrice ?? undefined}
                  onCommit={(min, max) => onParamsChange({ minPrice: min, maxPrice: max, page: 1 })}
                />
              </FilterSection>
              <Separator />
            </>
          )}

        {/* Brands */}
        <FilterSection
          title="יצרן"
          isOpen={openSections.has(-1)}
          onToggle={() => toggleSection(-1)}
        >
          <ExpandableFilterList items={brandFilterOptions} />
        </FilterSection>
        <Separator />

        {/* Tag Filters */}
        {orderedTagGroups.map((tagValues, index) => {
          if (!tagValues || tagValues.length === 0) return null;

          // Only show if has multiple values OR has selected value
          const hasSelectedValue = tagValues.some((tag) =>
            currentParams.filters?.includes(tag.valueId),
          );
          if (tagValues.length <= 1 && !hasSelectedValue) return null;

          return (
            <div key={tagValues[0].keyId}>
              <FilterSection
                title={tagValues[0].keyName}
                isOpen={openSections.has(index)}
                onToggle={() => toggleSection(index)}
              >
                <ExpandableFilterList items={createTagFilterOptions(tagValues)} />
              </FilterSection>
              <Separator />
            </div>
          );
        })}
      </div>
    </div>
  );
}
